/**
 * The money block the booking SUBMITS — the same five numbers the order
 * summary just displayed, stated explicitly so nothing downstream has to
 * reverse-engineer them.
 *
 * Until now the relay webhook guessed from `total_price`, a field that means
 * "net of the voucher" for pay-later but "pre-discount billed base" for a
 * membership signup. On a twice-a-year signup it reported gross 526 (list
 * prices, standalone test at 299) beside discount 68.20 (10% of the 682
 * MEMBER base) — two different bases in one payload, netting to 457.80,
 * which matches nothing: Stripe charged 613.80.
 *
 * Definitions, all in cents:
 *  - list:            the billed composition at list prices (the strikethrough
 *                     figure — add-ons at default price, the cadence at its own
 *                     price since a subscription has no list variant).
 *  - billed:          the pre-voucher base actually billed (member prices /
 *                     netted bundle / cadence) — the order summary's subtotal,
 *                     and the base the voucher applies to.
 *  - discount:        the exact voucher deduction, computed per billed line
 *                     the way Stripe computes it (voucherDiscountCents).
 *  - charged:         billed − discount — the Stripe invoice total, or the
 *                     expected clinic invoice for pay-later.
 *  - future_followup: the part of the cadence that pays for follow-up tests
 *                     months from now (cadence − yearly base): 200 on 2x, 600
 *                     on 4x, 0 otherwise. Carried so CRM/finance can defer it;
 *                     "today's appointment value" = billed − future_followup.
 */
import { voucherDiscountCents } from "./voucherDiscount";
import {
  memberContributionCents,
  isBloodMembershipBaseProduct,
} from "./bloodMembershipPricing";
import type { Product } from "@/components/product-step/types";
import type { Discount } from "@/stores/bookingStore";
import type { MembershipSignupSource } from "./membershipSignup";

export interface PricingSummary {
  list_cents: number;
  billed_cents: number;
  discount_cents: number;
  charged_cents: number;
  future_followup_cents: number;
  /**
   * EXISTING-member bookings only (member token): the basket at member rates —
   * list price with the member discount applied, entitled items included at
   * their member price rather than the 0 they are charged. Accounting wants
   * the deal to carry what the appointment is WORTH at member rates, not the
   * 0/extras-only figure that billed/charged describe. Absent elsewhere.
   */
  member_value_cents?: number;
}

export function buildPricingSummary(opts: {
  /** The billed composition at list prices. */
  listCents: number;
  /** One entry per amount Stripe bills (or the clinic invoices) — the discount
   *  is computed per line, matching Stripe's per-line rounding. */
  billedLines: number[];
  discount: Discount | null | undefined;
  /** The cadence's future-follow-up share; 0 for non-signup bookings. */
  futureFollowupCents: number;
  /** Existing-member bookings: the basket at member rates (see PricingSummary). */
  memberValueCents?: number;
}): PricingSummary {
  const billed = opts.billedLines.reduce((sum, line) => sum + line, 0);
  const discount = voucherDiscountCents(opts.discount, opts.billedLines);
  return {
    list_cents: opts.listCents,
    billed_cents: billed,
    discount_cents: discount,
    charged_cents: Math.max(0, billed - discount),
    future_followup_cents: opts.futureFollowupCents,
    ...(opts.memberValueCents !== undefined
      ? { member_value_cents: opts.memberValueCents }
      : {}),
  };
}

/**
 * cadence − baseline tier, floored at 0 — the deferred follow-up share.
 * Both prices come from Stripe (the membership tiers the catalog serves):
 * nothing is hardcoded, so a repricing of the baseline blood test moves the
 * split with it. With either price unknown the share is 0 — the deal then
 * simply isn't split, and value/paid still reconcile.
 */
export function futureFollowupCents(
  cadenceCents: number | undefined,
  baselineCents: number | undefined,
): number {
  if (!cadenceCents || !baselineCents) return 0;
  return Math.max(0, cadenceCents - baselineCents);
}

/**
 * An entitled follow-up blood test, redeemed from a 2x/4x membership, is worth
 * the share the signup deferred for it: 200 (2x: 499 - 299 for its one
 * follow-up; 4x: 600 for three). It also passes isBloodMembershipBaseProduct
 * (blood-only, with a draw), so without this it was counted as the yearly base
 * test at the 299 baseline, 99 above what the membership set aside for it.
 * A follow-up a member pays for counts at its member price (239), and a
 * non-member pays the list price (250), like any other product.
 */
const ENTITLED_FOLLOWUP_CENTS = 20000;
const isFollowupBloodTest = (p: Product): boolean =>
  p.stripe_metadata_id === "followup-blood-test";

export interface BilledComposition {
  listCents: number;
  billedLines: number[];
  futureFollowupCents: number;
  /** Existing-member bookings: the basket at member rates (see PricingSummary). */
  memberValueCents?: number;
}

/**
 * What this booking bills, per path — extracted from the submit hook so the
 * decision is testable, because each branch answers a different question:
 *
 *  - Blood signup: the base test is billed AS the cadence subscription and
 *    add-ons at member price (memberContributionCents — the displayed model);
 *    Stripe rounds a percentage coupon per invoice line, so the lines stay
 *    separate.
 *  - Scan-bundle signup: the caller passes bundle.lines verbatim — the exact
 *    amounts create-blood-checkout 409-guards the charge with.
 *  - Pay later / existing member: ONE aggregated line. Nothing is invoiced
 *    per-line by Stripe here, and the validator computes the recorded discount
 *    on the aggregate — splitting lines would let the summary drift a cent
 *    from the record at fractional percentages.
 */
export function billedComposition(opts: {
  cart: Product[];
  signup: MembershipSignupSource;
  isMember: boolean;
  /** Member/entitlement price resolver (getEffectivePrice). */
  effectivePriceOf: (p: Product) => number;
  cadenceCents: number | undefined;
  /** The baseline (once-a-year) tier's Stripe price — the part of any cadence
   *  that pays for THIS year's test; the remainder is the deferred share. */
  baselineCents: number | undefined;
  /** bundle.lines when the cart is a scan bundle, else null. */
  bundleLines: number[] | null;
}): BilledComposition {
  const { cart, signup, isMember, effectivePriceOf, cadenceCents } = opts;
  const listOf = (p: Product): number => p.default_price?.unit_amount ?? 0;

  if (signup !== "none") {
    const followup = futureFollowupCents(cadenceCents, opts.baselineCents);
    if (opts.bundleLines) {
      return {
        billedLines: opts.bundleLines,
        listCents:
          cart.reduce((sum, p) => sum + listOf(p), 0) + (cadenceCents ?? 0),
        futureFollowupCents: followup,
      };
    }
    return {
      billedLines: cart
        .map((p) => memberContributionCents(p, cadenceCents))
        .filter((cents) => cents > 0),
      // List view of the same composition: the base test IS the cadence
      // subscription (no list variant), everything else at list price.
      listCents: cart.reduce(
        (sum, p) =>
          sum +
          (isBloodMembershipBaseProduct(p) ? (cadenceCents ?? 0) : listOf(p)),
        0,
      ),
      futureFollowupCents: followup,
    };
  }

  const billed = cart.reduce(
    (sum, p) => sum + (isMember ? effectivePriceOf(p) : listOf(p)),
    0,
  );
  return {
    billedLines: billed > 0 ? [billed] : [],
    listCents: cart.reduce((sum, p) => sum + listOf(p), 0),
    futureFollowupCents: 0,
    // A member's deal value for accounting: the basket at member rates —
    // entitled items count at their member price, not the 0 they are charged.
    // An ENTITLED base test is the membership's own test, prepaid at the
    // baseline tier Stripe charges for it (299), not the catalogue
    // member_price (279) the base test gained later: that undercounted every
    // redemption by 20. A base test beyond the entitlement is bought at 279
    // and counts at 279.
    ...(isMember
      ? {
          memberValueCents: cart.reduce(
            (sum, p) =>
              sum +
              (isFollowupBloodTest(p) && effectivePriceOf(p) === 0
                ? ENTITLED_FOLLOWUP_CENTS
                : isBloodMembershipBaseProduct(p) &&
                    opts.baselineCents &&
                    effectivePriceOf(p) === 0
                  ? opts.baselineCents
                  : (p.member_price?.unit_amount ?? listOf(p))),
            0,
          ),
        }
      : {}),
  };
}
